import React, { useCallback } from 'react';

import { UniformSlot, type UniformSlotWrapperComponentProps } from '@uniformdev/canvas-react';

import {
	NavigationTabsSection,
	type NavigationTabDataType,
} from '@/libraries/design/patterns/navigation-tabs/src/navigation-tabs-section';

import { resolveNavigationTabHref } from '../../navigation-tab/src/uniform-navigation-tab';

type NavigationTabValue = string | boolean | { path: string };
type NavigationTabParameter = NavigationTabValue | { value?: NavigationTabValue };
type NavigationTabParameters = Record<string, NavigationTabParameter>;

const getNavigationTabParameters = (item: React.ReactNode): NavigationTabParameters | undefined => {
	if (!React.isValidElement(item)) return undefined;

	const props = item.props as {
		data?: { parameters?: NavigationTabParameters };
		children?: React.ReactNode;
	};
	if (props.data?.parameters) return props.data.parameters;

	return React.Children.toArray(props.children)
		.map(getNavigationTabParameters)
		.find((parameters) => parameters !== undefined);
};

const getParameterValue = (parameter?: NavigationTabParameter): NavigationTabValue | undefined => {
	if (typeof parameter === 'string' || typeof parameter === 'boolean' || parameter === undefined)
		return parameter;
	if ('path' in parameter) return parameter;
	return parameter.value;
};

const getStringParameterValue = (parameter?: NavigationTabParameter): string | undefined => {
	const value = getParameterValue(parameter);
	return typeof value === 'string' ? value : undefined;
};

const getLinkParameterValue = (
	parameter?: NavigationTabParameter,
): string | { path: string } | undefined => {
	const value = getParameterValue(parameter);
	return typeof value === 'boolean' ? undefined : value;
};

export interface UniformNavigationTabsProps {
	appearance: 'wide' | 'compact';
	layout?: 'stacked' | 'overflow';
	eyebrow?: string;
	showControls?: boolean;
	useHistoryRouting?: boolean;
}
const UniformNavigationTabs = ({
	appearance,
	layout = 'stacked',
	eyebrow,
	showControls,
	useHistoryRouting = false,
}: UniformNavigationTabsProps) => {
	const renderNavigationTabs: React.ComponentType<UniformSlotWrapperComponentProps> = useCallback(
		({ items }) => {
			const tabsData = items.flatMap((item, index) => {
				const params = getNavigationTabParameters(item);
				const href = resolveNavigationTabHref(
					getLinkParameterValue(params?.['href']),
					getStringParameterValue(params?.['urlSlug']),
				);

				if (!href) return [];

				return [
					{
						id:
							(getParameterValue(params?.['id']) as string | undefined) ??
							`navigation-tab-${index}`,
						title: (getParameterValue(params?.['title']) ??
							getParameterValue(params?.['label'])) as string,
						href,
						iconUrl: getParameterValue(params?.['iconUrl']) as string | undefined,
						iconSize: getParameterValue(params?.['iconSize']) as NavigationTabDataType['iconSize'],
						disableIconPadding: getParameterValue(params?.['disableIconPadding']) as
							| boolean
							| undefined,
						tabComponent: item,
					} as NavigationTabDataType,
				];
			});

			return (
				<NavigationTabsSection
					tabsData={tabsData}
					appearance={appearance}
					layout={layout}
					eyebrow={eyebrow}
					showControls={showControls}
					useHistoryRouting={useHistoryRouting}
				/>
			);
		},
		[appearance, eyebrow, layout, showControls, useHistoryRouting],
	);

	return <UniformSlot name="navigationItems" wrapperComponent={renderNavigationTabs} />;
};
export default UniformNavigationTabs;
